import { cva } from "class-variance-authority";
/**
 * Button variants using class-variance-authority.
 * All colors reference CSS variable tokens via Tailwind config.
 *
 * Material Design 3 Button Specifications:
 * - Filled: High emphasis, primary action
 * - Filled Tonal: Medium emphasis, secondary action
 * - Outlined: Medium emphasis with border
 * - Text: Low emphasis, tertiary action
 * - Elevated: High emphasis with shadow
 */
export const buttonVariants = cva(
// Base styles applied to all buttons
[
    "inline-flex",
    "items-center",
    "justify-center",
    "gap-2",
    "font-medium",
    "rounded-md-full",
    "transition-all",
    "duration-md-medium",
    "ease-md-standard",
    "focus:outline-none",
    "focus-visible:ring-2",
    "focus-visible:ring-offset-2",
    "focus-visible:ring-primary",
    "disabled:cursor-not-allowed",
    "select-none",
    "whitespace-nowrap",
], {
    variants: {
        variant: {
            filled: [
                "bg-primary",
                "text-on-primary",
                "hover:shadow-md-1",
                "hover:brightness-110",
                "active:brightness-90",
                // Only apply disabled gray styles when not loading
                "[&:disabled:not([data-loading])]:bg-on-surface/[0.12]",
                "[&:disabled:not([data-loading])]:text-on-surface/[0.38]",
                "[&:disabled:not([data-loading])]:shadow-none",
                // Keep normal colors when loading
                "[&[data-loading]]:bg-primary",
                "[&[data-loading]]:text-on-primary",
                "[&[data-loading]]:opacity-100",
            ],
            filledTonal: [
                "bg-secondary-container",
                "text-on-secondary-container",
                "hover:shadow-md-1",
                "hover:brightness-105",
                "active:brightness-95",
                "disabled:bg-on-surface/[0.12]",
                "disabled:text-on-surface/[0.38]",
                "disabled:shadow-none",
            ],
            outlined: [
                "bg-white",
                "text-primary",
                "border",
                "border-primary",
                "hover:bg-primary/[0.08]",
                "active:bg-primary/[0.12]",
                "disabled:border-on-surface/[0.12]",
                "disabled:text-on-surface/[0.38]",
            ],
            text: [
                "bg-transparent",
                "text-primary",
                // No state-layer fill: text actions stay visually flat (link-style).
                "hover:bg-transparent",
                "active:bg-transparent",
                "disabled:text-on-surface/[0.38]",
            ],
            elevated: [
                "bg-surface-container-low",
                "text-primary",
                "shadow-md-1",
                "hover:shadow-md-2",
                "hover:bg-primary/[0.08]",
                "active:shadow-md-1",
                "active:bg-primary/[0.12]",
                "disabled:bg-on-surface/[0.12]",
                "disabled:text-on-surface/[0.38]",
                "disabled:shadow-none",
            ],
        },
        size: {
            small: [
                "py-2",
                "px-3",
                "text-[14px]",
                "leading-[20px]",
                "tracking-[0.1px]",
                "min-w-[64px]",
                "font-sans",
            ],
            medium: [
                "py-3",
                "px-6",
                "text-title-md",
                "min-w-[80px]",
                "font-sans",
            ],
            large: [
                "py-4",
                "px-8",
                "text-title-md",
                "min-w-[96px]",
                "font-sans",
            ],
        },
        fullWidth: {
            true: "w-full",
            false: "",
        },
    },
    defaultVariants: {
        variant: "filled",
        size: "medium",
        fullWidth: false,
    },
});
/**
 * Icon wrapper styles for consistent sizing and spacing
 */
export const iconVariants = cva("flex-shrink-0", {
    variants: {
        size: {
            small: "w-4 h-4",
            medium: "w-5 h-5",
            large: "w-6 h-6",
        },
    },
    defaultVariants: {
        size: "medium",
    },
});
/**
 * Spinner size dimensions only (for placeholder/spacer; no animation).
 * Use spinnerVariants for the visible spinner.
 */
export const spinnerSizeVariants = cva("", {
    variants: {
        size: {
            small: "w-3 h-3",
            medium: "w-4 h-4",
            large: "w-5 h-5",
        },
    },
    defaultVariants: {
        size: "medium",
    },
});
/**
 * Loading spinner styles
 */
export const spinnerVariants = cva("animate-spin", {
    variants: {
        size: {
            small: "w-3 h-3",
            medium: "w-4 h-4",
            large: "w-5 h-5",
        },
    },
    defaultVariants: {
        size: "medium",
    },
});
//# sourceMappingURL=button.styles.js.map